import { useEffect, useMemo } from "react";
import { useDialogs } from "@toolpad/core";
import keyBy from "lodash-es/keyBy";

import type { FindingRow } from "../types";
import { useTriageToolbar } from "~/providers/triage-toolbar";
import { useExceptionsButtons } from "~/components/exceptions/use-exceptions-buttons";
import { useExceptions } from "~/components/exceptions/use-exceptions";
import { useFetchExceptions } from "~/components/exceptions/use-fetch-exceptions";
import { TriageToolbarButtonType } from "~/components/TriageToolbar/types";
import { ScopeType, useScope } from "~/hooks/useScope";
import { useSecurityPipelineIntegrations } from "~/pages/integrations/security-pipeline/hooks/useSecurityPipelineIntegrations";
import {
  SetExceptionDialog,
  SetExceptionDialogPayload,
  SelectedEntityWithFindingData,
} from "~/components/exceptions/SetExceptionDialog";
import { RemoveExceptionsGroupDialog } from "~/components/exceptions/RemoveExceptionsGroupDialog";
import { CreateSecurityPipelinePrDialog } from "~/pages/integrations/security-pipeline/components/CreatePullRequest/CreateSecurityPipelinePRDialog";
import { useTickets } from "~/pages/tickets/hooks";
import { useTicketingIntegrations } from "~/pages/tickets/hooks/useTicketingIntegrations";
import { CreateTicketDialog } from "~/pages/tickets/components/CreateTicketDialog";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import {
  ExceptionType,
  GetCasesCountDocument,
  GetFindingsDocument,
  GetAssetInsightsDocument,
  LoadAssetDocument,
} from "~/operations";
import { sortFindingsToExceptionEntitiesMrns } from "~/pages/inventory/components/Findings/FindingsTable/utils/findingTableUtils";
import { SelectedEntityWithException } from "~/components/exceptions/types";
import { CVEFindingsNode } from "~/pages/inventory/components/AssetSoftwareDetails/AssetSoftwareVulnerabilities/hooks/types";
import { AdvisoryFindingsNode } from "~/pages/inventory/components/Findings/FindingsTable/utils/advisoryTypes";

export type UseDataGridTriageToolbarParams = {
  findings: FindingRow[];
  selectedMrns: string[];
  onSelectionClear: () => void;
  entityMrn: string;
  assetId?: string;
  assetName?: string;
  assetMrn?: string;
  totalCount?: number;
};

export function useDataGridTriageToolbar({
  findings,
  selectedMrns,
  onSelectionClear,
  entityMrn,
  assetId,
  assetName,
  assetMrn,
  totalCount,
}: UseDataGridTriageToolbarParams) {
  const { activeScope: scope, spaceScope } = useScope();
  const dialogs = useDialogs();
  const triageToolbar = useTriageToolbar();
  const { hasSecurityPipelineIntegrations } = useSecurityPipelineIntegrations();
  const { hasTicketingIntegration } = useTicketingIntegrations();
  const { isFeatureEnabled } = useFeatureFlags();

  const {
    handleCreateTicketingIntegrationModalOpen,
    loading: createTicketLoading,
    handleCreateCase,
    hasCreateTicketPermissions,
  } = useTickets({
    createCaseRefetchQueries: [GetCasesCountDocument],
    onTicketCreate: () => onSelectionClear(),
  });

  // Build a lookup map for findings by MRN (for CVE/Advisory findings)
  const findingsByMrn = keyBy(
    findings.filter(
      (
        item,
      ): item is FindingRow & {
        __typename: "CveFinding" | "AdvisoryFinding";
      } =>
        item.__typename === "CveFinding" ||
        item.__typename === "AdvisoryFinding",
    ),
    "mrn",
  );

  // Create a Set for O(1) lookup
  const selectedMrnsSet = useMemo(() => new Set(selectedMrns), [selectedMrns]);

  // Convert selection to entities with exception data
  const selectedEntities = useMemo(() => {
    return findings
      .filter((f) => selectedMrnsSet.has(f.mrn))
      .map(
        (f): SelectedEntityWithFindingData & SelectedEntityWithException => ({
          mrn: f.mrn,
          exception: f.exception,
          title: f.title || f.id,
          rating: f.rating ?? undefined,
          type: f.__typename as
            | "CheckFinding"
            | "CveFinding"
            | "AdvisoryFinding"
            | "GenericFinding",
        }),
      );
  }, [findings, selectedMrnsSet]);

  const hasGenericFindingsSelected = useMemo(
    () => selectedEntities.some((entity) => entity.type === "GenericFinding"),
    [selectedEntities],
  );

  const {
    exceptionsButtonsAvailability,
    exceptionsButtonsVisibility,
    exceptionsButtonsTooltipText,
  } = useExceptionsButtons({
    selectedEntities,
    target: "finding",
    showExceptionButtons: true,
    showScopeButtons: false,
  });

  const { exceptionGroups } = useFetchExceptions({
    scopeMrn: entityMrn,
    skip: !entityMrn,
    types: [
      ExceptionType.Cve,
      ExceptionType.Advisory,
      ExceptionType.Security,
      ExceptionType.Fex,
    ],
  });

  const {
    handleRemoveExceptionGroups,
    handleSetException,
    loading: exceptionsLoading,
  } = useExceptions({
    onSetException: async () => {
      onSelectionClear();
    },
    scopeMrns: [entityMrn],
    ...sortFindingsToExceptionEntitiesMrns({ selectedEntities }),
    refetchQueries: [
      GetFindingsDocument,
      GetAssetInsightsDocument,
      LoadAssetDocument,
    ],
  });

  const handleSetExceptionModalOpen = async () => {
    const exceptionDialogPayload: SetExceptionDialogPayload = {
      target: "asset",
      selectedEntities: selectedEntities,
      onSave: handleSetException,
      shouldListEntities: true,
      targetData: {
        targetTitle: assetName,
        onAsset: {
          id: assetId || "",
          mrn: assetMrn || "",
          name: assetName || "",
          __typename: "EntityInfoAsset",
        },
      },
    };

    dialogs.open(SetExceptionDialog, exceptionDialogPayload);
  };

  const handleRemoveExceptionModalOpen = () => {
    dialogs.open(RemoveExceptionsGroupDialog, {
      onRemove: async (params) => {
        await handleRemoveExceptionGroups(params);
        onSelectionClear();
      },
      exceptionMrns: exceptionGroups.map((eg) => eg.mrn),
    });
  };

  const handleCreateRemediationPRDialogOpen = () => {
    if (!spaceScope || !scope) return;

    // Filter to only CVE/Advisory findings for remediation PR
    // FindingRow is a subset of CVEFindingsNode | AdvisoryFindingsNode.
    // The dialog only uses mrn, title, and spaceRiskDelta from findings,
    // so this cast is safe. A narrower dialog prop type would remove the
    // need for this cast entirely.
    const cveAdvisoryFindings = selectedEntities
      .filter((e) => ["CveFinding", "AdvisoryFinding"].includes(e.type || ""))
      .map((entity) => findingsByMrn[entity.mrn])
      .filter(Boolean) as unknown as Array<
      CVEFindingsNode | AdvisoryFindingsNode
    >;

    dialogs.open(CreateSecurityPipelinePrDialog, {
      spaceScope: spaceScope,
      findings: cveAdvisoryFindings,
      ...(assetMrn ? { assetMrns: [assetMrn] } : {}),
    });
  };

  const handleCreateTicketDialogOpen = () => {
    if (
      !spaceScope ||
      !scope ||
      (scope.type !== ScopeType.Space && scope.type !== ScopeType.Workspace)
    )
      return;

    dialogs.open(CreateTicketDialog, {
      isCombinedTicket: isFeatureEnabled("Multi-finding ticketing"),
      target: "asset",
      loading: createTicketLoading,
      space: spaceScope,
      scope,
      hasCreateCasePermissions: hasCreateTicketPermissions,
      onSave: (data) =>
        handleCreateCase({
          data,
          refetchQueries: [
            GetFindingsDocument,
            GetAssetInsightsDocument,
            LoadAssetDocument,
          ],
        }),
      selectedEntities: selectedEntities.map((entity) => ({
        mrn: entity.mrn,
        scopeMrn: scope?.mrn,
      })),
    });
  };

  // Show/hide toolbar based on selection
  useEffect(() => {
    if (selectedEntities.length > 0) {
      triageToolbar.show({
        buttons: [
          // CreateTicket button
          {
            type: TriageToolbarButtonType.CreateTicket,
            onClick: !hasTicketingIntegration
              ? handleCreateTicketingIntegrationModalOpen
              : handleCreateTicketDialogOpen,
            disabled: hasGenericFindingsSelected,
            tooltip: hasGenericFindingsSelected
              ? "Mondoo cannot create tickets for generic findings"
              : undefined,
          },
          // CreateException button
          ...(exceptionsButtonsVisibility.SET_EXCEPTION
            ? [
                {
                  type: TriageToolbarButtonType.CreateException,
                  onClick: handleSetExceptionModalOpen,
                  disabled: !exceptionsButtonsAvailability.SET_EXCEPTION,
                  tooltip: exceptionsButtonsTooltipText.SET_EXCEPTION,
                },
              ]
            : []),
          // RemoveException button
          ...(exceptionsButtonsVisibility.REMOVE_EXCEPTION
            ? [
                {
                  type: TriageToolbarButtonType.RemoveException,
                  onClick: handleRemoveExceptionModalOpen,
                  disabled: !exceptionsButtonsAvailability.REMOVE_EXCEPTION,
                  tooltip: exceptionsButtonsTooltipText.REMOVE_EXCEPTION,
                },
              ]
            : []),
          // CreateRemediationWorkflow button (if security pipeline integrations exist)
          ...(scope?.type === ScopeType.Space && hasSecurityPipelineIntegrations
            ? [
                {
                  type: TriageToolbarButtonType.CreateRemediationWorkflow,
                  onClick: handleCreateRemediationPRDialogOpen,
                  disabled: selectedEntities.some((e) => {
                    return !["CveFinding", "AdvisoryFinding"].includes(
                      e.type || "",
                    );
                  }),
                  tooltip:
                    "Mondoo cannot create workflows for check and generic findings",
                },
              ]
            : []),
        ],
        payload: {
          count: selectedEntities.length,
          totalCount,
          onClose: onSelectionClear,
        },
      });
    } else {
      triageToolbar.hide();
    }

    return () => {
      triageToolbar.hide();
    };
  }, [selectedEntities]);

  return {
    selectedEntities,
    exceptionsLoading,
  };
}
